'use client';

import { LinkWithChildren } from 'mode-shared/components/Header';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { useToggle } from 'react-use';
import { twMerge } from 'tailwind-merge';

export const MobileMenu = ({ links, className }: { links: LinkWithChildren[]; className: string }) => {
  const currentRoute = usePathname();
  const [open, toggleOpen] = useToggle(false);

  const preparedNavLinks = useMemo(() => {
    const result: Array<{
      parent?: string;
      text: string;
      href?: string;
      disabled?: boolean;
      target?: string;
      isParent?: boolean;
      hide?: boolean;
    }> = [];
    links.forEach((link) => {
      if (link.children && link.children.length) {
        result.push({
          text: link.text,
          isParent: true,
        });
        link.children.forEach((child) => {
          result.push({ ...child, parent: link.text });
        });
      } else {
        result.push(link);
      }
    });
    return result;
  }, [links]);

  return (
    <div className={twMerge(className, open ? 'z-50' : '')}>
      <div className="p-4" onClick={toggleOpen}>
        <svg className="h-6 w-6" viewBox="0 0 24 24">
          <line
            className={`stroke-white stroke-2 transition-all duration-200 ease-out origin-[1px_6px] ${open ? 'rotate-45' : ''}`}
            x1={0}
            x2={24}
            y1={4}
            y2={4}
          />
          <line
            className={`stroke-white stroke-2 transition-all duration-200 ease-out ${open ? 'opacity-0' : 'opacity-100'}`}
            x1={0}
            x2={24}
            y1={12}
            y2={12}
          />
          <line
            className={`stroke-white stroke-2 transition-all duration-200 ease-out origin-[2px_18px] ${open ? '-rotate-45' : ''}`}
            x1={0}
            x2={24}
            y1={20}
            y2={20}
          />
        </svg>
      </div>
      <div
        className={twMerge(
          `transition-transform overflow-x-hidden duration-500 ease-in-out transform translate-y-full overflow-y-auto overscroll-contain touch-pan-y w-full h-[calc(100vh-80px)] bg-neutral-1000 fixed top-[80px] left-0 flex flex-col px-6 pb-[80px] gap-4`,
          open && 'translate-y-0'
        )}
      >
        {preparedNavLinks.map(({ text, href, disabled, target, isParent, parent, hide }, ix) => {
          if (hide) {
            return null;
          }

          if (isParent) {
            return (
              <div key={`parent-${text}-${ix}`} className="text-neutral-400 font-bold text-base mt-4 mb-1 pl-4">
                {text}
              </div>
            );
          }
          if (disabled) {
            return (
              <div key={`${text}-${ix}`} className="text-neutral-100 font-medium opacity-40 pointer-events-none ml-4">
                {text}
              </div>
            );
          }

          if (href) {
            return (
              <Link
                key={href}
                href={href}
                target={target}
                className={`flex items-center w-full text-neutral-100 font-medium text-lg px-4 py-1 rounded-md ml-${parent ? '4' : '0'}
                        ${currentRoute === href ? 'text-primary bg-neutral-900' : ''}`}
                onClick={toggleOpen}
              >
                {text}
              </Link>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};
